// Booking with the bundle: the order v1's basket checkout sends when the
// bundle pays (POST /widget/app/confirmFreeBasketOrder with `bundleId`), the
// items in it (from the Timetable tab's selection, or from the shared
// checkout's "proceed" payload in bundle mode), and what the order's status
// says once it's sent (GET /widget/app/basketOrderStatus/:id).

import type { BundleDoc, ManageSchool, UserFormAnswer } from "./types";
import type { SelectionEntry } from "./timetableSelection";

export type TicketSelection = { ticketTypeId: string; ticketTypeName: string; quantity: number; price: number };

/** A booking in the order (buildBasketItemsPayload's fields for one). */
export type RedemptionItem = {
  id: string;
  type: "booking";
  schoolId: string;
  courseId: string;
  courseName?: string;
  sessionId?: string;
  sessionDate?: string;
  sessionEndDate?: string;
  timezone?: string;
  redeemedFromBundleId: string;
  ticketSelections: TicketSelection[];
  addOns: unknown[];
  quantity: number;
  /** The tickets' real prices (the bundle zeroes the order, not the item). */
  price: number;
  currency: string;
  bookingType: string;
  checkoutGroupId: string;
  imageUrl?: string;
  isOnline?: boolean;
  userInputs?: unknown[];
  participants?: unknown[];
  guestDetails?: unknown;
  checkoutGuestsInformation?: unknown[];
  appointmentData?: unknown;
};

/** A fresh id for an item or a checkout group. */
export function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const priceOf = (tickets: TicketSelection[]) => tickets.reduce((sum, t) => sum + (t.price || 0) * (t.quantity || 0), 0);

/** The Timetable tab's selection as order items. */
export function itemsFromSelection(
  entries: SelectionEntry[],
  ctx: {
    school: ManageSchool;
    bundleId: string;
    answers: Record<string, UserFormAnswer[]>;
    timezoneFor: (entry: SelectionEntry) => string | undefined;
    id?: () => string;
  },
): RedemptionItem[] {
  const id = ctx.id ?? newId;
  return entries.map((entry) => {
    const { session } = entry;
    const ticketSelections = entry.tickets.map((t) => {
      const option = session.bookingOptions?.find((o) => o.ticketTypeID === t.ticketTypeID);
      return {
        ticketTypeId: t.ticketTypeID,
        ticketTypeName: option?.ticketTypeName || "Ticket",
        quantity: t.quantity,
        price: option?.price ?? 0,
      };
    });
    return {
      id: id(),
      type: "booking",
      schoolId: ctx.school._id,
      courseId: session.courseID ?? session.course?._id ?? "",
      courseName: session.course?.title,
      sessionId: session._id,
      sessionDate: session.startDateTime,
      sessionEndDate: session.endDateTime,
      timezone: ctx.timezoneFor(entry),
      redeemedFromBundleId: ctx.bundleId,
      ticketSelections,
      addOns: [],
      quantity: entry.tickets.reduce((sum, t) => sum + t.quantity, 0),
      price: priceOf(ticketSelections),
      currency: ctx.school.preferredCurrency ?? "GBP",
      bookingType: "regular",
      checkoutGroupId: id(),
      isOnline: session.isOnline,
      userInputs: ctx.answers[session._id] ?? [],
    };
  });
}

/** What the shared checkout hands over when it's done (the fields read). */
export type CheckoutItemData = {
  courseId: string;
  courseName?: string;
  sessionId?: string;
  sessionDate?: string;
  timezone?: string;
  ticketSelections?: TicketSelection[];
  addOns?: unknown[];
  appointmentData?: unknown;
  guestDetails?: unknown;
  checkoutGuestsInformation?: unknown[];
  userInputs?: unknown[];
  participants?: unknown[];
  currency?: string;
  imageUrl?: string;
  isOnline?: boolean;
  bookingType?: string;
  checkoutGroupId?: string;
};

/** The shared checkout's booking as an order item (v1's CheckoutOverlay in
 * bundle mode: the price rebuilt from the tickets' real prices). */
export function itemFromCheckout(
  data: CheckoutItemData,
  ctx: { schoolId: string; bundleId: string; currency: string; id?: () => string },
): RedemptionItem {
  const id = ctx.id ?? newId;
  const ticketSelections = data.ticketSelections ?? [];
  return {
    id: id(),
    type: "booking",
    schoolId: ctx.schoolId,
    courseId: data.courseId,
    courseName: data.courseName,
    sessionId: data.sessionId,
    sessionDate: data.sessionDate,
    timezone: data.timezone,
    redeemedFromBundleId: ctx.bundleId,
    ticketSelections,
    addOns: data.addOns ?? [],
    appointmentData: data.appointmentData,
    guestDetails: data.guestDetails,
    checkoutGuestsInformation: data.checkoutGuestsInformation,
    userInputs: data.userInputs,
    participants: data.participants,
    price: priceOf(ticketSelections),
    currency: data.currency || ctx.currency,
    imageUrl: data.imageUrl,
    isOnline: data.isOnline,
    quantity: 1,
    bookingType: data.bookingType || "regular",
    checkoutGroupId: data.checkoutGroupId || id(),
  };
}

/** Tickets in an order (the spots it takes when the caller doesn't know
 * the guests per ticket). */
export const itemTickets = (items: RedemptionItem[]) =>
  items.reduce((sum, item) => sum + item.ticketSelections.reduce((s, t) => s + (t.quantity || 0), 0), 0);

export type Buyer = { email: string; firstName: string; lastName: string; phone: string; userId?: string };

/** "Ada Lovelace King" → Ada / Lovelace King. */
export function splitName(name: string | undefined): { firstName: string; lastName: string } {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstName: words[0] ?? "", lastName: words.slice(1).join(" ") };
}

/** Who the bookings are for: the bundle's holder, linked to the signed-in
 * account when it's theirs. */
export function buyerFromBundle(bundle: BundleDoc, user: { id: string; email: string } | null | undefined): Buyer {
  const email = (bundle.buyerEmail ?? "").trim();
  const sameAccount = !!user && !!email && user.email.trim().toLowerCase() === email.toLowerCase();
  return {
    email,
    ...splitName(bundle.buyerName),
    phone: bundle.buyerPhone ?? "",
    ...(sameAccount ? { userId: user!.id } : {}),
  };
}

/** What's missing before the order can go. */
export function buyerProblems(buyer: Buyer): string[] {
  const problems: string[] = [];
  if (!buyer.email) problems.push("This bundle has no email address. Please contact us to book with it.");
  if (!buyer.firstName.trim()) problems.push("Please enter a first name.");
  if (!buyer.lastName.trim()) problems.push("Please enter a last name.");
  return problems;
}

export function redemptionBody(args: {
  items: RedemptionItem[];
  buyer: Buyer;
  bundleId: string;
  sourceInfo?: unknown;
}) {
  const { email, firstName, lastName, phone, userId } = args.buyer;
  return {
    items: args.items,
    buyer: { email, firstName: firstName.trim(), lastName: lastName.trim(), phone, ...(userId ? { userId } : {}) },
    source: "widget",
    sourceInfo: args.sourceInfo,
    bundleId: args.bundleId,
  };
}

/** A refused order: the API's message and, per item, why. */
export class RedemptionError extends Error {
  constructor(
    message: string,
    readonly itemMessages: Array<{ itemId: string; message: string }> = [],
  ) {
    super(message);
    this.name = "RedemptionError";
  }
}

const FALLBACK = "We couldn’t book that with your bundle. Please try again.";

/** The refusal in a non-2xx answer's body. */
export function readRedemptionFailure(body: unknown): RedemptionError {
  const fields = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  const text = (key: string) => (typeof fields[key] === "string" && fields[key] ? (fields[key] as string) : undefined);
  const items = Array.isArray(fields.unavailableItems) ? fields.unavailableItems : [];
  const itemMessages = items.flatMap((entry) => {
    const e = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};
    return typeof e.message === "string" && e.message ? [{ itemId: String(e.itemId ?? ""), message: e.message }] : [];
  });
  return new RedemptionError(text("ux_message") ?? text("error") ?? text("message") ?? FALLBACK, itemMessages);
}

/** Sends the order; its id. */
export async function confirmRedemption(api: string, body: ReturnType<typeof redemptionBody>, init: RequestInit = {}): Promise<string> {
  let res: Response;
  try {
    res = await fetch(`${api}/widget/app/confirmFreeBasketOrder`, {
      ...init,
      method: "POST",
      headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
      body: JSON.stringify(body),
    });
  } catch {
    throw new RedemptionError("We couldn’t reach us just now. Please check your connection and try again.");
  }
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) throw readRedemptionFailure(json);
  const id = json && typeof json === "object" ? (json as { basketOrderId?: unknown }).basketOrderId : undefined;
  if (typeof id !== "string" || !id) throw new RedemptionError(FALLBACK);
  return id;
}

export type OrderStatus = {
  paymentStatus?: string;
  allItemsCompleted?: boolean;
  processingStats?: { total?: number; pending?: number; processing?: number; completed?: number; failed?: number };
};

export type OrderOutcome = "pending" | "confirmed" | "partial" | "failed";

/** Where an order is: still being made, made, partly made, or not. */
export function orderOutcome(status: OrderStatus | undefined): OrderOutcome {
  if (!status) return "pending";
  if (status.allItemsCompleted) return "confirmed";
  const s = status.processingStats ?? {};
  const open = (s.pending ?? 0) + (s.processing ?? 0);
  if ((s.failed ?? 0) > 0 && open === 0) return (s.completed ?? 0) > 0 ? "partial" : "failed";
  if (status.paymentStatus === "FAILED") return "failed";
  return "pending";
}

/** How long an order is watched before the page says it's still going. */
export const ORDER_POLL_MS = 3000;
export const ORDER_POLL_LIMIT = 40;

/** A watched order's query state (react-query's counters). */
export type OrderPollState = { data?: OrderStatus; dataUpdateCount?: number; errorUpdateCount?: number };

/** Status checks made so far, failed ones included. */
export const pollAttempts = (state: OrderPollState | undefined) => (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);

export type OrderWatch = {
  outcome: OrderOutcome;
  /** Still pending after the last check the page makes: it stops asking. */
  stalled: boolean;
  /** Whether to ask again. */
  poll: boolean;
};

/** Where a watched order stands, and whether to keep asking. A check that
 * fails counts like one that answers, so an endpoint that keeps failing
 * still ends in "stalled" instead of being asked forever. */
export function watchOrder(state: OrderPollState | undefined, limit = ORDER_POLL_LIMIT): OrderWatch {
  const outcome = orderOutcome(state?.data);
  const used = pollAttempts(state) >= limit;
  return { outcome, stalled: outcome === "pending" && used, poll: outcome === "pending" && !used };
}
